import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import {
  CaretDownIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  FilePdfIcon,
} from '@phosphor-icons/react';
import type {
  WorkbookDefinition,
  WorkbookReportOutput,
} from '@erp/shared-types';
import { toast } from 'sonner';
import { orpc, rpcClient } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { hydrateLayout } from '@/components/report-canvas/hydrate';
import { deserializeLayout } from '@/components/report-canvas/serialization';
import { vegaWidgetRegistry } from '@/components/report-canvas/vega/VegaChartWidget';
import type { WorkbookReportData } from './useWorkbookReportData';
import { chartedSources } from './WorkbookReportCanvas';
import { workbookReportRegistry } from './workbookReportRegistry';
import { useWorkbookRunTime } from './workbookRunTime';
import {
  REPORT_PRINT_PART,
  WorkbookReportPrint,
  type WorkbookReportPrintHeader,
} from './WorkbookReportPrint';
import {
  bytesToBase64,
  captureReportParts,
  captureReportPng,
  waitForQuiet,
} from './workbookReportCapture';
import { buildReportPdf, reportPdfFileName } from './workbookReportPdf';

type ExportKind = 'preview' | 'download' | 'email';
/** One click's export; its identity tells a new one from a re-render. */
type ExportJob = { kind: ExportKind };
type Rendered = { pdf: Uint8Array; png: string | null };

/** The Report's title in its PDF and email: its own title, else the Output's name. */
export function reportExportTitle(
  output: Pick<WorkbookReportOutput, 'name' | 'title'>,
): string {
  return output.title?.trim() || output.name;
}

/** The vega stack its charts load lazily, loaded ahead so none render after the capture. */
function loadVegaCharts() {
  return import('@/components/report-canvas/vega/VegaChartInner');
}

function saveBlob(pdf: Uint8Array, fileName: string) {
  const url = URL.createObjectURL(
    new Blob([pdf.slice().buffer], { type: 'application/pdf' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  // After the click has handed the file to the browser.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Preview or download a Report as a PDF, or email it to yourself as a test.
 * The browser renders it: the same charts over the same Report datasets
 * viewers see, off screen at the page's width, captured part by part so a
 * page never cuts through a component. Scheduled Report emails will run as
 * workflows; this sends only to the requesting user.
 */
export function WorkbookReportExport({
  workbookId,
  definition,
  output,
  data,
  save,
}: {
  workbookId: string;
  definition: WorkbookDefinition;
  output: WorkbookReportOutput;
  data: WorkbookReportData;
  /** Saves the Definition, so the server finds the Report it is asked to email. */
  save: () => Promise<unknown>;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const runTime = useWorkbookRunTime(definition);
  const [job, setJob] = useState<ExportJob | null>(null);
  // The job whose render has started: a re-render re-attaches the ref, and must not render again.
  const started = useRef<ExportJob | null>(null);
  const [preview, setPreview] = useState<{
    url: string;
    pdf: Uint8Array;
  } | null>(null);
  const layout = useMemo(
    () =>
      hydrateLayout(
        deserializeLayout(output.layout, workbookReportRegistry),
        workbookReportRegistry,
      ),
    [output.layout],
  );
  const title = reportExportTitle(output);
  const fileName = reportPdfFileName(title);
  const sources = chartedSources(output.layout, data);
  const ranAt = sources
    .flatMap((source) => (source.ranAt ? [source.ranAt] : []))
    .sort()
    .at(-1);
  const header: WorkbookReportPrintHeader = {
    title,
    description: output.description?.trim() || undefined,
    subtitle: ranAt
      ? gt('{workbook} · Results as of {time}', {
          context: 'workbook report pdf subtitle',
          workbook: definition.name,
          time: runTime.full(ranAt),
        })
      : definition.name,
  };

  const deliver = useCallback(
    async (kind: ExportKind, rendered: Rendered) => {
      if (kind === 'download') return saveBlob(rendered.pdf, fileName);
      if (kind === 'preview') {
        const url = URL.createObjectURL(
          new Blob([rendered.pdf.slice().buffer], { type: 'application/pdf' }),
        );
        return setPreview({ url, pdf: rendered.pdf });
      }
      await save();
      const { sentTo } = await rpcClient.workbooks.sendReportTestEmail({
        workbookId,
        outputId: output.id,
        pdfBase64: bytesToBase64(rendered.pdf),
        pngBase64: rendered.png ?? '',
      });
      toast.success(
        gt('Test email sent to {address}.', {
          context: 'workbook report test email sent',
          address: sentTo,
        }),
      );
    },
    [fileName, gt, output.id, save, workbookId],
  );

  // Renders once the print layout mounts: a DOM node to measure is the trigger.
  const printRef = useCallback(
    (root: HTMLDivElement | null) => {
      if (!root || !job || started.current === job) return;
      started.current = job;
      const { kind } = job;
      async function render(element: HTMLDivElement): Promise<Rendered> {
        await waitForQuiet(
          element,
          () =>
            queryClient.isFetching({
              queryKey: orpc.workbooks.resultRows.key(),
            }) +
              queryClient.isFetching({
                queryKey: orpc.workbooks.aggregate.key(),
              }) >
            0,
        );
        const parts = [
          ...element.querySelectorAll<HTMLElement>(`[${REPORT_PRINT_PART}]`),
        ];
        const pdf = await buildReportPdf(
          await captureReportParts(
            parts,
            getComputedStyle(element).backgroundColor,
          ),
          title,
        );
        return {
          pdf,
          png: kind === 'email' ? await captureReportPng(element) : null,
        };
      }
      render(root)
        .then((rendered) => deliver(kind, rendered))
        .catch((error: unknown) =>
          toast.error(
            error instanceof Error && kind === 'email'
              ? error.message
              : gt("The report's PDF couldn't be made. Try again.", {
                  context: 'workbook report pdf failed',
                }),
          ),
        )
        .finally(() => setJob(null));
    },
    [job, queryClient, title, deliver, gt],
  );

  async function start(kind: ExportKind) {
    if (layout.some((item) => item.component.type in vegaWidgetRegistry))
      await loadVegaCharts();
    setJob({ kind });
  }
  function closePreview() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            disabled={job !== null}
          >
            {job ? (
              <Spinner className="h-4 w-4" />
            ) : (
              <FilePdfIcon className="h-4 w-4" />
            )}
            {job?.kind === 'email' ? (
              <T context="workbook report export sending">Sending…</T>
            ) : job ? (
              <T context="workbook report export rendering">Rendering PDF…</T>
            ) : (
              <T context="workbook report export">PDF</T>
            )}
            <CaretDownIcon className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => void start('preview')}>
            <EyeIcon />
            <T context="workbook report export">Preview PDF</T>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void start('download')}>
            <DownloadSimpleIcon />
            <T context="workbook report export">Download PDF</T>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => void start('email')}>
            <EnvelopeSimpleIcon />
            <T context="workbook report export">Send test email</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {job && (
        <WorkbookReportPrint
          ref={printRef}
          layout={layout}
          data={data}
          header={header}
          neverRun={sources.every((source) => source.path === 'none')}
          paletteId={output.paletteId}
          filters={output.filters}
        />
      )}
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => !open && closePreview()}
      >
        <DialogContent className="flex h-[90vh] flex-col sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {preview && (
            // Our own PDF, from a blob: URL; a sandboxed frame won't show a PDF.
            // react-doctor-disable-next-line react-doctor/iframe-missing-sandbox
            <iframe
              src={preview.url}
              title={gt('Report PDF preview', {
                context: 'workbook report pdf preview',
              })}
              // Opaque until the PDF viewer paints: a transparent frame shows
              // the Report behind the dialog while it opens.
              className="min-h-0 w-full flex-1 rounded border bg-muted"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePreview}>
              <T context="workbook report pdf preview close">Close</T>
            </Button>
            <Button onClick={() => preview && saveBlob(preview.pdf, fileName)}>
              <DownloadSimpleIcon className="h-4 w-4" />
              <T context="workbook report export">Download PDF</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
